import { StoryblokComponent, useStoryblokState } from '@storyblok/react';
import type { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import React, { useEffect } from 'react';

import { FooterBlock, HeaderBlock } from '@/blocks';
import { Breadcrumb } from '@/components/modules';
import type { RoutingPath, StoryblokPageProps, StoryblokParams } from '@/lib';
import { useSearchState, useStoryState } from '@/providers';
import {
  type StoryblokPreview,
  fetchContactModule,
  fetchLanguagesByMarket,
  fetchNavigation,
  fetchStoryBySlug,
  flatPropertiesDataConverter,
  getPageLinks,
  getProperties,
  getPropertiesPreview,
  getPropertyParamsFromPropertyPreview,
  getPropertyParamsFromStory,
  getSlugFromPath,
} from '@/utils';

const Home: NextPage<StoryblokPageProps> = ({
  baseSlug,
  ...props
}: StoryblokPageProps) => {
  const { contactModule, breadcrumbPaths } = props;
  const { header, footer } = props.navigation || {};
  const story = useStoryblokState(props.story, {}, props.preview);
  const { setSearchState } = useSearchState();
  const { setStoryState } = useStoryState();

  const pageData = story?.content;

  const shouldAddContactModule =
    !!contactModule && !pageData.disabledGlobalContactModule;

  useEffect(() => {
    setSearchState({
      properties: flatPropertiesDataConverter(props.properties || []),
      propertiesPreview: props.propertiesPreview || null,
    });

    setStoryState({
      preview: props.preview,
      baseSlug,
      pageLanguages: props.pageLanguages,
      alternates: story.alternates,
      marketConfig: props.marketConfig,
      story,
    });
  }, [
    props.preview,
    props.properties,
    setSearchState,
    setStoryState,
    baseSlug,
    props.pageLanguages,
    story.alternates,
    story.full_slug,
    story,
    props.marketConfig,
    props.propertiesPreview,
  ]);

  const breadcrumb = breadcrumbPaths
    ?.filter((item) => item)
    .map((item) => ({
      href: `/${item.full_slug}`,
      label: item.content.breadcrumb_display_name ?? item.name,
      id: item.id,
    }));

  return (
    <>
      {!!header && <HeaderBlock blok={header} baseSlug={baseSlug} />}
      <StoryblokComponent
        blok={story.content}
        story={story}
        contactModule={shouldAddContactModule && contactModule}
      />
      {breadcrumb?.length > 1 && <Breadcrumb items={breadcrumb} />}
      {!!footer && <FooterBlock blok={footer} />}
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const { pageLinks } = await getPageLinks();
  const paths: RoutingPath[] = pageLinks.map(({ splittedSlug }) => {
    return { params: { slug: splittedSlug } };
  });
  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<
  StoryblokPageProps,
  StoryblokParams,
  StoryblokPreview
> = async (context) => {
  const { params, previewData: { version } = {} } = context;
  const slug = params?.slug.join('/') || '';

  const { prefix, language, tenant } = slug
    ? getSlugFromPath(slug)
    : { prefix: '', language: '', tenant: '' };

  const story = await fetchStoryBySlug(slug, {
    version,
  });

  if (!story) {
    console.error(`This page could not be found: ${slug}`);
    return { notFound: true, revalidate: 1 };
  }

  const navigation = prefix ? await fetchNavigation(prefix) : null;
  const contactModule = prefix ? await fetchContactModule(prefix) : null;
  const marketConfig = tenant
    ? await fetchStoryBySlug(`${tenant}/config`, {})
    : null;

  const breadcrumbUrlSlug: string[] =
    story?.full_slug.split('/').filter((emptyValue) => emptyValue) ?? [];

  const breadcrumbArray: string[] = [];
  for (let index = 0; index < breadcrumbUrlSlug?.length; index++) {
    breadcrumbArray.push(
      `${
        typeof breadcrumbArray[index - 1] !== 'undefined'
          ? breadcrumbArray[index - 1]
          : ''
      }/${breadcrumbUrlSlug[index]}`
    );
  }

  const fetchedBreadcrumbStories: Array<void> = [];
  for (let i = 1; i < breadcrumbArray.length; i++) {
    fetchedBreadcrumbStories.push(
      (await fetchStoryBySlug(breadcrumbArray[i], {
        version,
      })) as unknown as void
    );
  }

  const breadcrumbPaths = (await Promise.all(fetchedBreadcrumbStories).then(
    (values) => {
      return values.filter((value) => value !== undefined);
    }
  )) as StoryblokPageProps['breadcrumbPaths'];
  const { folderLinks } = await getPageLinks();

  // Get languages from storyblok structure.
  const pageLanguages = tenant
    ? fetchLanguagesByMarket(tenant, folderLinks)
    : null;

  if (!pageLanguages) {
    console.error(`Languages for the page could not be found: ${slug}`);
  }

  const propertyParams = getPropertyParamsFromStory(story);
  const propertyPreviewParams = getPropertyParamsFromPropertyPreview(story);

  const propertiesPreview = propertyPreviewParams
    ? (await getPropertiesPreview(propertyPreviewParams, language, tenant))[0]
    : null;

  const properties = propertyParams
    ? await getProperties(propertyParams)
    : null;

  return {
    props: {
      baseSlug: prefix,
      story,
      contactModule,
      marketConfig,
      properties: properties || [],
      propertiesPreview: propertiesPreview || null,
      navigation,
      key: story ? story.id : false,
      preview: version === 'draft',
      pageLanguages: pageLanguages ?? [],
      breadcrumbPaths: breadcrumbPaths ?? [],
      ...(await serverSideTranslations(language ?? 'en')),
    },
  };
};

export default Home;
